import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { CompanyIcon, fmtPrice } from '../CompanyCard';
import { useApp } from '../../context/AppContext';
import { toast } from 'sonner';
import { X, TrendingUp, Wallet, ShieldAlert } from 'lucide-react';

export default function BuyModal({ company, open, onClose }) {
  const { vaultBalance, kycStatus, buyShares } = useApp();
  const minShares = company ? Math.ceil(company.minLot / company.lastPrice) : 0;
  const [shares, setShares] = useState(minShares);

  useEffect(() => {
    if (company) setShares(Math.ceil(company.minLot / company.lastPrice));
  }, [company]);

  if (!company) return null;
  const price = company.ask;
  const total = shares * price;
  const fee = total * 0.0075;
  const grand = total + fee;

  const handleBuy = async () => {
    if (kycStatus !== 'verified') {
      toast.error('Accreditation required', { description: 'Complete SEC 506(c) KYC before transacting.' });
      return;
    }
    if (shares < minShares) {
      toast.error('Below minimum lot', { description: `Minimum order is ${minShares.toLocaleString()} shares.` });
      return;
    }
    const res = await buyShares(company, shares, price);
    if (!res.ok) {
      if (res.reason === 'kyc') {
        toast.error('Accreditation required', { description: 'Complete SEC 506(c) KYC before transacting.' });
      } else {
        toast.error('Insufficient vault balance', { description: 'Add funds to your vault to complete this order.' });
      }
      return;
    }
    toast.success('Order filled', {
      description: `Bought ${shares.toLocaleString()} ${company.ticker} @ ${fmtPrice(price)}`,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--panel)] border-[var(--border)] text-[var(--text)] max-w-md p-0 gap-0 [&>button]:hidden">
        <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#1c1710] border border-[#3a2a18] flex items-center justify-center">
              <CompanyIcon name={company.icon} size={18} />
            </div>
            <div>
              <h3 className="font-display font-semibold text-white text-[15px]">Buy {company.ticker}</h3>
              <div className="text-[11px] font-mono text-[var(--muted)]">Ask {fmtPrice(price)} • Min {minShares.toLocaleString()} sh</div>
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--muted)] hover:text-white p-1"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {kycStatus !== 'verified' && (
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-[#2a1a0e] border border-[#4a2f14] text-[12px]">
              <ShieldAlert size={16} className="text-[var(--orange)] mt-0.5 shrink-0" />
              <span className="text-[#f0c99a]">
                {kycStatus === 'pending'
                  ? 'Your KYC is under review. Trading unlocks once our compliance desk approves your accreditation (within 24 hours).'
                  : kycStatus === 'rejected'
                  ? 'Your KYC was rejected. Please re-submit accreditation from the top bar to enable settlement.'
                  : 'Accreditation not verified. Complete SEC 506(c) KYC from the top bar to enable settlement.'}
              </span>
            </div>
          )}

          <div>
            <label className="text-[10px] font-mono tracking-widest text-[var(--muted-2)]">SHARES</label>
            <input
              type="number"
              value={shares}
              min={minShares}
              onChange={(e) => setShares(Math.max(0, parseInt(e.target.value || '0')))}
              className="w-full mt-1.5 bg-[var(--bg)] border border-[var(--border)] rounded-lg px-3 py-2.5 font-mono text-[16px] text-white focus:outline-none focus:border-[var(--orange)] transition-colors"
            />
          </div>

          <div className="space-y-2 text-[13px] font-mono">
            <Line k="Notional" v={fmtPrice(total)} />
            <Line k="ATS Fee (0.75%)" v={fmtPrice(fee)} />
            <div className="h-px bg-[var(--border)] my-1" />
            <Line k="Total" v={fmtPrice(grand)} bold />
            <Line k="Vault Balance" v={`$${vaultBalance.toLocaleString()}`} muted />
          </div>

          <button
            onClick={handleBuy}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-[var(--green)] text-[#052e20] font-semibold text-[14px] hover:bg-[#13b878] transition-colors"
          >
            <TrendingUp size={16} /> PLACE BUY ORDER
          </button>
          <div className="flex items-center justify-center gap-1.5 text-[10px] font-mono text-[var(--muted-2)]">
            <Wallet size={12} /> SETTLED VIA VAULT ESCROW • SIMULATED
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Line({ k, v, bold, muted }) {
  return (
    <div className="flex items-center justify-between">
      <span className={muted ? 'text-[var(--muted-2)]' : 'text-[var(--muted)]'}>{k}</span>
      <span className={bold ? 'text-white font-semibold text-[15px]' : muted ? 'text-[var(--muted)]' : 'text-[var(--text)]'}>{v}</span>
    </div>
  );
}
